import { Link } from "react-router-dom";
import { Navigation, Footer, ScriptureBlock } from "@/components/Layout";
import { Scale, Eye, Heart, Globe, Hammer, RefreshCw } from "lucide-react";
import { SEO } from "@/components/SEO";
import { ScrollReveal } from "@/hooks/useScrollReveal";

const principles = [
  {
    icon: Scale,
    title: "Truth and Disclosure",
    verse: "A false balance is abomination to the Lord: but a just weight is his delight.",
    reference: "Proverbs 11:1",
    description: "We tell the truth about what we know. We don't hide defects, overstate values, or use misleading language. Every deal includes full disclosure of all known facts."
  },
  {
    icon: Eye,
    title: "No Hidden Spreads / Honest Compensation",
    verse: "Providing for honest things, not only in the sight of the Lord, but also in the sight of men.",
    reference: "2 Corinthians 8:21",
    description: "We disclose our compensation in writing on every transaction. You'll always know exactly how we make money—no hidden markups, no mysterious fees."
  },
  {
    icon: Heart,
    title: "Respect for Image-Bearers",
    verse: "Thou shalt love thy neighbour as thyself.",
    reference: "Matthew 22:39",
    description: "Every person we work with bears the image of God. We treat sellers, buyers, and partners with dignity, patience, and respect—regardless of the deal size."
  },
  {
    icon: Globe,
    title: "Stewardship, Not Ownership",
    verse: "The earth is the Lord's, and the fulness thereof; the world, and they that dwell therein.",
    reference: "Psalm 24:1",
    description: "We don't own land—we steward it. Every parcel belongs to God. Our role is to facilitate His purposes, not to maximize our gain at others' expense."
  },
  {
    icon: RefreshCw,
    title: "Repentance and Correction",
    verse: "If we confess our sins, he is faithful and just to forgive us our sins, and to cleanse us from all unrighteousness.",
    reference: "1 John 1:9",
    description: "When we make mistakes—and we will—we own them. We don't hide behind legal language or blame others. We repent, correct, and make it right."
  },
  {
    icon: Hammer,
    title: "Prayer and Dependence on God",
    verse: "Trust in the Lord with all thine heart; and lean not unto thine own understanding.",
    reference: "Proverbs 3:5",
    description: "This business exists by God's grace. We pray over decisions, seek wisdom from Scripture, and depend on Him for every outcome—not our own cleverness."
  }
];

export default function Faith() {
  return (
    <div className="min-h-screen bg-white" data-testid="faith-page">
      <SEO 
        title="Faith & Operating Principles"
        description="Steward Lots faith-based operating principles for ethical land wholesaling. Built on Scripture with truth, disclosure, and integrity. Jesus Christ is Lord over this Christ-led land stewardship business."
        path="/faith"
      />
      <Navigation mode="builder" />

      {/* Hero */}
      <section className="pt-24 pb-16 px-6 md:px-12 bg-[#0F0F10]" data-testid="faith-hero">
        <div className="max-w-4xl mx-auto text-center">
          <div className="live-indicator justify-center text-sm text-[#A8A29E] mb-6 gentle-reveal">
            <span className="live-dot" aria-hidden="true"></span>
            <span>Governing principles in active practice</span>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl text-white mb-6 gentle-reveal" style={{ animationDelay: '0.2s' }}>
            Faith & Operating Principles
          </h1>
          <p className="text-[#A8A29E] text-lg max-w-2xl mx-auto gentle-reveal" style={{ animationDelay: '0.4s' }}>
            This business is operated by a follower of Jesus Christ. Jesus is Lord over this work.
          </p>
        </div>
      </section>

      {/* Confession */}
      <ScrollReveal animation="reveal-up">
        <section className="py-16 px-6 md:px-12 bg-[#F5F5F4]" data-testid="confession-section">
          <div className="max-w-3xl mx-auto">
            <article className="bg-white p-8 md:p-12 border border-[#E7E5E4]">
              <h2 className="font-serif text-2xl md:text-3xl text-[#1C1917] mb-6 text-center">
                Our Confession
              </h2>
              <div className="space-y-4 text-[#57534E] text-lg">
                <p>
                  This business exists to glorify God through honest, faithful stewardship of land. We confess Jesus Christ as Lord—not just in church, but in every contract, conversation, and transaction.
                </p>
                <p>
                  We do not separate our faith from our business. The same God who commands us to love our neighbor also commands us to use honest scales. The same Christ who died for sinners calls us to integrity in all things.
                </p>
                <p className="font-medium text-[#1C1917]">
                  We would rather lose a deal than violate God's commands.
                </p>
              </div>
            </article>
          </div>
        </section>
      </ScrollReveal>

      {/* Covenant Accountability Model */}
      <ScrollReveal animation="reveal-up">
        <section className="py-16 px-6 md:px-12" data-testid="covenant-section">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-serif text-3xl md:text-4xl text-[#1C1917] mb-6 text-center">
              The Covenant Accountability Model
          </h2>
          <div className="text-[#57534E] space-y-4">
            <p>
              Steward Lots™ is built on a covenant-based approach to land stewardship rooted in Scripture. 
              We believe land, capital, and opportunity are entrusted resources, not commodities to exploit. 
              This conviction shapes everything we do as a Christ-led land stewardship business.
            </p>
            <p>
              Our <strong>Covenant Accountability Model</strong> requires full transparency with all parties, 
              clear disclosure of intent (including assignments), fair pricing grounded in real builder math, 
              and the freedom to walk away from any deal that violates conscience or integrity. This standard 
              governs how every land opportunity is evaluated, structured, and executed.
            </p>
            <p>
              For <Link to="/seller" className="text-[#BFA15F] hover:underline">land sellers</Link>, this means 
              you will always know our compensation, our intentions, and everything we know about your property. 
              For <Link to="/builder" className="text-[#BFA15F] hover:underline">builders and land buyers</Link>, 
              this means clean deals with disclosed economics and no hidden spreads.
            </p>
            <p>
              We reject the common industry practices of manipulation, pressure tactics, and obscured margins. 
              Ethical land wholesaling is not an oxymoron—it is possible when Christ-centered principles guide 
              every decision. We exist to prove that land facilitation can be done with integrity.
            </p>
          </div>
        </div>
      </section>
    </ScrollReveal>

      {/* Non-negotiables */}
      <section className="py-24 px-6 md:px-12 bg-[#F5F5F4]" data-testid="principles-section">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal animation="reveal-up">
            <div className="text-center mb-16">
              <h2 className="font-serif text-3xl md:text-4xl text-[#1C1917] mb-4">
                Non-Negotiable Principles of Land Stewardship
              </h2>
              <p className="text-[#57534E] text-lg max-w-2xl mx-auto">
                These principles are rooted in Scripture. They're not marketing—they're how we actually operate 
                in every interaction with land sellers, builders, and land buyers.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {principles.map((principle, index) => (
              <ScrollReveal key={index} animation="reveal-up" delay={index % 4 + 1}>
                <article 
                  className="scripture-card interactive-card bg-white p-8 border border-[#E7E5E4]"
                  data-testid={`principle-card-${index}`}
                >
                  <principle.icon className="w-8 h-8 text-[#BFA15F] mb-4" aria-hidden="true" />
                  <h3 className="font-serif text-xl text-[#1C1917] mb-4">{principle.title}</h3>
                <ScriptureBlock
                  verse={principle.verse}
                  reference={principle.reference}
                  className="mb-4"
                />
                <p className="text-[#57534E]">{principle.description}</p>
              </article>
            </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why This Matters */}
      <ScrollReveal animation="reveal-up">
        <section className="py-16 px-6 md:px-12" data-testid="why-matters-section">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-serif text-3xl md:text-4xl text-[#1C1917] mb-6 text-center">
              Why Faith-Based Land Stewardship Matters
            </h2>
            <div className="text-[#57534E] space-y-4">
              <p>
                The land industry has earned a reputation for aggressive tactics, hidden margins, and 
                exploitation of uninformed sellers. Many land wholesalers operate without ethical guardrails, 
                prioritizing quick profits over honest dealing. This harms real people—often those who can 
                least afford to be taken advantage of.
              </p>
              <p>
                At Steward Lots™, we believe there is a better way. Scripture provides clear commands about 
                honest business practices: use just weights, love your neighbor, provide for honest things 
              before God and men. These are not suggestions—they are commands from the God who created 
              the land we steward.
            </p>
            <p>
              Our faith compels us to treat every land seller as an image-bearer of God deserving dignity 
              and truth. It compels us to treat every builder and land buyer as a partner deserving fair 
              dealing. This is not idealism—it is obedience. And we believe God honors businesses that 
              operate under His authority.
            </p>
            <p>
              <Link to="/about" className="text-[#BFA15F] hover:underline">Learn more about our founder</Link> and 
              the vision behind Steward Lots™.
            </p>
          </div>
        </div>
      </section>
    </ScrollReveal>

      {/* Final Statement */}
      <section className="py-24 px-6 md:px-12 bg-[#0F0F10]" data-testid="final-statement">
        <ScrollReveal animation="reveal-scale">
          <div className="max-w-3xl mx-auto text-center">
            <div className="border-l-[3px] border-[#BFA15F] pl-6 text-left inline-block">
              <p className="font-serif italic text-2xl md:text-3xl text-white mb-4">
                "We would rather lose a deal than violate God's commands."
              </p>
              <p className="text-[#A8A29E] uppercase tracking-wide text-sm">
                Our Commitment
              </p>
            </div>
            <p className="text-[#A8A29E] text-lg mt-12 max-w-xl mx-auto">
              This isn't aspirational language. It's the line we won't cross. Every deal, every time.
            </p>
            <div className="mt-8">
              <Link to="/contact" className="text-[#BFA15F] hover:underline">
                Contact us to learn more about working with Steward Lots™
              </Link>
            </div>
          </div>
        </ScrollReveal>
      </section>

      <Footer />
    </div>
  );
}
